/**
 * The CPV list pager: "Showing 1–25 of 109", the page sizes the server allows, previous / next and the page numbers.
 *
 * Every number is the server's page answer ({count, page, page_size, pages, page_sizes} — services/cpv/api/pagination.py,
 * sized by `cpv.api.page_size*`); nothing is counted or sized here. `usePageParams` keeps the page and its size in the
 * URL (as the dashboard filters are), so Back from a row returns to the same page; a filter change drops `page` (page 1)
 * and keeps the size.
 */
import React, { useCallback, useMemo } from 'react';
import { Select } from 'antd';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';

export const PAGE_PARAM = 'page';
export const PAGE_SIZE_PARAM = 'page_size';
const GAP = 'gap';

/** The page numbers to offer: the first, the last, the current one and its neighbours; a gap between runs. */
export function pageWindow(page, pages) {
  const keep = new Set([1, pages, page - 1, page, page + 1].filter((n) => n >= 1 && n <= pages));
  const out = [];
  [...keep].sort((a, b) => a - b).forEach((n, i, all) => {
    if (i > 0 && n - all[i - 1] > 1) out.push(`${GAP}-${n}`);
    out.push(n);
  });
  return out;
}

/** True when the server refused the page asked for (past the last one, after rows went away): go back to page 1. */
export const pastLastPage = (err) => err?.response?.status === 400 && err?.response?.data?.field === PAGE_PARAM;

/** The URL's parameters without `page` — `setParams(withoutPage, {replace: true})` goes back to page 1. */
export const withoutPage = (params) => {
  const next = new URLSearchParams(params);
  next.delete(PAGE_PARAM);
  return next;
};

/** {paging: {page?, page_size?} for the request, setPage(page, size?)}, read from and written to the URL. */
export function usePageParams() {
  const [params, setParams] = useSearchParams();
  const page = params.get(PAGE_PARAM) || undefined;
  const size = params.get(PAGE_SIZE_PARAM) || undefined;
  const paging = useMemo(() => ({ ...(page ? { page } : {}), ...(size ? { page_size: size } : {}) }), [page, size]);
  const setPage = useCallback((nextPage, nextSize) => {
    const next = new URLSearchParams(params);
    if (nextPage > 1) next.set(PAGE_PARAM, String(nextPage)); else next.delete(PAGE_PARAM);
    if (nextSize) next.set(PAGE_SIZE_PARAM, String(nextSize));
    setParams(next, { replace: true });
  }, [params, setParams]);
  return { paging, setPage };
}

/**
 * `data`: the server's page answer. `onChange(page, pageSize)`: a new page, `pageSize` only when the reader picked a new
 * size (which starts again at page 1); a page number alone keeps the size the URL has.
 * Renders nothing while there is no page or no row.
 */
export default function Pager({ data, onChange, testId = 'cu-pager', label = 'Pages' }) {
  if (!data || !data.count) return null;
  const { count, page, page_size: size, pages } = data;
  const first = (page - 1) * size + 1;
  const last = Math.min(page * size, count);
  const sizes = (data.page_sizes || []).includes(size) ? data.page_sizes : [...(data.page_sizes || []), size].sort((a, b) => a - b);
  return (
    <nav className="cu-pager" aria-label={label} data-testid={testId}>
      <span className="cu-pager-range" data-testid={`${testId}-range`}>
        Showing <b>{first}–{last}</b> of <b>{count}</b>
      </span>
      <div className="cu-pager-controls">
        {sizes.length > 1 && (
          <Select aria-label="Rows per page" value={size} popupMatchSelectWidth={false} data-testid={`${testId}-size`}
            options={sizes.map((n) => ({ value: n, label: `${n} / page` }))} onChange={(n) => onChange(1, n)} />
        )}
        {pages > 1 && (
          <>
            <button type="button" className="cu-pager-btn" aria-label="Previous page" disabled={page <= 1}
              data-testid={`${testId}-prev`} onClick={() => onChange(page - 1)}>
              <ChevronLeft size={14} aria-hidden />
            </button>
            {pageWindow(page, pages).map((n) => (typeof n === 'string'
              ? <span key={n} className="cu-pager-gap" aria-hidden>…</span>
              : (
                <button key={n} type="button" className="cu-pager-btn" aria-current={n === page ? 'page' : undefined}
                  aria-label={`Page ${n}`} data-testid={`${testId}-page-${n}`} onClick={() => n !== page && onChange(n)}>
                  {n}
                </button>
              )))}
            <button type="button" className="cu-pager-btn" aria-label="Next page" disabled={page >= pages}
              data-testid={`${testId}-next`} onClick={() => onChange(page + 1)}>
              <ChevronRight size={14} aria-hidden />
            </button>
          </>
        )}
      </div>
    </nav>
  );
}
